import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { usePageTitle } from '../../../../lib/hooks';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Button } from '../../../../components/ui/Button.js';
import { Input } from '../../../../components/ui/FormField.js';
import { EntityActionMenu } from '../../../../components/ui/EntityActionMenu.js';
import { Search, MapPin, FolderPlus, Plus, Trash2, Landmark } from 'lucide-react';
import { useToast } from '../../../../components/ui/Toast.js';

import { useAreaGroups, useAreas, useDeleteAreaGroup, useDeleteArea } from '../../../../features/settings/network/areas.api';

export const Route = createFileRoute('/_auth/settings/areas/')({
  component: AreasSettingsPage,
});

interface AreaGroup {
  id: string;
  name: string;
  code: string;
}

interface Area {
  id: string;
  name: string;
  code: string;
  areaGroupId: string;
}

function AreasSettingsPage() {
  usePageTitle('Areas Settings');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: groupsRes } = useAreaGroups();
  const groups: AreaGroup[] = Array.isArray(groupsRes) ? groupsRes : (groupsRes?.data ?? []);
  const { data: areasRes } = useAreas();
  const areas: Area[] = Array.isArray(areasRes) ? areasRes : (areasRes?.data ?? []);
  const [selectedGroupId, setSelectedGroupId] = React.useState<string>('');

  // Search states
  const [groupQuery, setGroupQuery] = React.useState('');
  const [areaQuery, setAreaQuery] = React.useState('');

  const deleteGroupMutation = useDeleteAreaGroup();
  const deleteAreaMutation = useDeleteArea();

  React.useEffect(() => {
    if (selectedGroupId && !groups.some(g => g.id === selectedGroupId)) {
      setSelectedGroupId(groups[0]?.id || '');
    }
  }, [groups]);

  const handleDeleteGroup = (id: string) => {
    const hasLinkedAreas = areas.some(a => a.areaGroupId === id);
    if (hasLinkedAreas) {
      toast({
        type: 'error',
        title: 'Action Blocked',
        description: 'Cannot delete group with active areas. Reassign or delete the areas first.',
      });
      return;
    }
    deleteGroupMutation.mutate(id, {
      onSuccess: () => {
        toast({ type: 'success', title: 'Deleted', description: 'Area group deleted.' });
      },
      onError: (err: unknown) => {
        toast({ type: 'error', title: 'Error', description: err instanceof Error ? err.message : 'Failed to delete area group.' });
      }
    });
  };

  const handleDeleteArea = (id: string) => {
    deleteAreaMutation.mutate(id, {
      onSuccess: () => {
        toast({ type: 'success', title: 'Deleted', description: 'Area deleted.' });
      },
      onError: (err: unknown) => {
        toast({ type: 'error', title: 'Error', description: err instanceof Error ? err.message : 'Failed to delete area.' });
      }
    });
  };

  const filteredGroups = groups.filter(g =>
    g.name.toLowerCase().includes(groupQuery.toLowerCase()) ||
    g.code.toLowerCase().includes(groupQuery.toLowerCase())
  );

  const filteredAreas = areas.filter(a =>
    a.areaGroupId === selectedGroupId && (
      a.name.toLowerCase().includes(areaQuery.toLowerCase()) ||
      a.code.toLowerCase().includes(areaQuery.toLowerCase())
    )
  );

  const selectedGroup = groups.find(g => g.id === selectedGroupId);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Regional Area Master Data"
        subtitle="Organize customers, agents, and collection fields into hierarchical Area Groups and Areas."
        action={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate({ to: '/settings/areas/new-group' })}>
              <FolderPlus className="w-4 h-4 mr-2" />
              Add Area Group
            </Button>
            <Button variant="primary" onClick={() => navigate({ to: '/settings/areas/new' })} disabled={groups.length === 0}>
              <Plus className="w-4 h-4 mr-2" />
              Add Area
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left Column: Area Groups */}
        <div className="lg:col-span-1 space-y-4">
          <div className="flex items-center justify-between bg-surface-2 p-3 rounded-lg border border-border">
            <h3 className="font-semibold text-text-primary text-sm flex items-center gap-1.5">
              <Landmark className="w-4 h-4 text-accent-500" />
              Area Groups ({groups.length})
            </h3>
          </div>

          <Input
            type="text"
            placeholder="Search groups..."
            value={groupQuery}
            onChange={e => setGroupQuery(e.target.value)}
            startIcon={<Search className="w-3.5 h-3.5" />}
            className="h-8 text-xs"
          />

          <div className="space-y-2 max-h-[450px] overflow-y-auto pr-1">
            {filteredGroups.map(g => (
              <div
                key={g.id}
                onClick={() => setSelectedGroupId(g.id)}
                className={`p-3.5 rounded-lg border text-left transition-all cursor-pointer flex items-center justify-between ${
                  selectedGroupId === g.id
                    ? 'bg-accent-50/70 border-accent-300 text-text-primary ring-1 ring-accent-300'
                    : 'bg-surface-1 border-border text-text-secondary hover:bg-surface-2'
                }`}
              >
                <div>
                  <h4 className="font-bold text-sm">{g.name}</h4>
                  <span className="font-mono text-caption uppercase text-text-muted tracking-wider block mt-0.5">Code: {g.code}</span>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <EntityActionMenu
                    actions={[
                      {
                        label: 'Delete Group',
                        icon: <Trash2 className="w-4 h-4" />,
                        variant: 'destructive',
                        requiresConfirmation: true,
                        confirmTitle: 'Delete Area Group',
                        confirmDescription: 'Are you sure you want to delete the area group "' + g.name + '"? All linked areas must be reassigned or deleted first.',
                        confirmLabel: 'Delete Group',
                        onClick: () => handleDeleteGroup(g.id),
                      },
                    ]}
                  />
                </div>
              </div>
            ))}
            {filteredGroups.length === 0 && (
              <p className="text-center text-xs text-text-muted py-6">No area groups found.</p>
            )}
          </div>
        </div>

        {/* Right Column: Areas inside selected group */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-surface-2 p-3.5 rounded-lg border border-border flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-text-primary text-sm flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-accent-500" />
                Areas in "{selectedGroup?.name || 'Group'}"
              </h3>
              <p className="text-xs text-text-muted mt-0.5">
                Displaying specific local neighborhoods mapped to this regional division.
              </p>
            </div>
            <span className="font-mono text-xs font-bold text-accent-600 bg-accent-50 px-2 py-0.5 rounded border border-accent-100">
              {selectedGroup?.code || '—'}
            </span>
          </div>

          <Input
            type="text"
            placeholder="Search local areas..."
            value={areaQuery}
            onChange={e => setAreaQuery(e.target.value)}
            startIcon={<Search className="w-3.5 h-3.5" />}
            className="h-8 text-xs max-w-sm"
          />

          <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
            <table className="w-full border-collapse text-left text-sm">
              <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                <tr>
                  <th className="p-4">Area Name</th>
                  <th className="p-4">Area Code</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-text-primary">
                {filteredAreas.map(a => (
                  <tr key={a.id} className="hover:bg-surface-2 transition-colors">
                    <td className="p-4 font-semibold">{a.name}</td>
                    <td className="p-4 font-mono text-xs text-text-secondary">{a.code}</td>
                    <td className="p-4 text-right">
                      <EntityActionMenu
                        actions={[
                          {
                            label: 'Delete Area',
                            icon: <Trash2 className="w-4 h-4" />,
                            variant: 'destructive',
                            requiresConfirmation: true,
                            confirmTitle: 'Delete Area',
                            confirmDescription: 'Are you sure you want to delete "' + a.name + '"? This action cannot be undone.',
                            confirmLabel: 'Delete Area',
                            onClick: () => handleDeleteArea(a.id),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
                {filteredAreas.length === 0 && (
                  <tr>
                    <td colSpan={3} className="p-8 text-center text-text-muted">No areas registered under this group. Click 'Add Area' to register one.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
